import { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { adminApi } from "@/services/adminApi";
import { RefreshCcw } from "lucide-react";

interface QueueInfo {
  queueName: string;
  userId: string;
  waiting: number;
  active: number;
  delayed: number;
  completed: number;
  failed: number;
}

interface StatusCount {
  status: string;
  count: number;
}

interface ActiveSync {
  connectorId: string;
  name: string;
  type: string;
  userName: string;
  userEmail: string;
  orgName: string;
  totalItems: number;
  completed: number;
  failed: number;
  pending: number;
  progress: number;
}

interface RecentFailure {
  connectorId: string;
  name: string;
  type: string;
  lastSynced: string | null;
  userName: string;
  userEmail: string;
  orgName: string;
}

interface FailedItem {
  id: string;
  name: string;
  retryCount: number;
  updatedAt: string;
  connectorName: string;
  connectorType: string;
}

interface FailedJob {
  jobId: string;
  queueName: string;
  connectorDataId: string | null;
  connectorId: string | null;
  failedReason: string;
  stacktrace: string[];
  timestamp: number | null;
  attemptsMade: number;
}

interface WorkerStatus {
  queues: {
    total: number;
    active: QueueInfo[];
    totals: {
      waiting: number;
      active: number;
      delayed: number;
      completed: number;
      failed: number;
    };
  };
  connectors: {
    statusCounts: StatusCount[];
  };
  connectorData: {
    statusCounts: StatusCount[];
  };
  activeSyncs: ActiveSync[];
  recentFailures: RecentFailure[];
  recentFailedItems: FailedItem[];
  failedJobs: FailedJob[];
  timestamp: string;
}

const STATUS_COLORS: Record<string, "default" | "success" | "warning" | "error" | "info"> = {
  syncCompleted: "success",
  syncStarted: "warning",
  syncFailed: "error",
  connected: "info",
  completed: "success",
  pending: "warning",
  failed: "error",
};

const AUTO_REFRESH_MS = 10_000;

export default function WorkerDashboardPage() {
  const [data, setData] = useState<WorkerStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const result = await adminApi.getWorkerStatus();
      setData(result);
      setLastRefresh(new Date());
    } catch (error) {
      console.error("Failed to fetch worker status:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(fetchData, AUTO_REFRESH_MS);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchData]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-lg text-gray-500">Loading worker status...</div>
      </div>
    );
  }

  if (!data) return null;

  const totalPending = data.queues.totals.waiting + data.queues.totals.active + data.queues.totals.delayed;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Worker Dashboard</h1>
          {lastRefresh && (
            <p className="text-xs text-gray-400 mt-1">
              Last updated: {lastRefresh.toLocaleTimeString()}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded"
            />
            Auto-refresh (10s)
          </label>
          <Button variant="outline" size="sm" onClick={fetchData}>
            <RefreshCcw className="h-4 w-4 mr-1" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          label="Active Queues"
          value={data.queues.active.length}
          sublabel={`${data.queues.total} total discovered`}
          color="blue"
        />
        <StatCard
          label="Jobs In-Flight"
          value={data.queues.totals.active}
          sublabel={`${totalPending} total pending`}
          color={data.queues.totals.active > 0 ? "amber" : "green"}
        />
        <StatCard
          label="Active Syncs"
          value={data.activeSyncs.length}
          sublabel="connectors syncing now"
          color={data.activeSyncs.length > 0 ? "blue" : "gray"}
        />
        <StatCard
          label="Failed (24h)"
          value={data.recentFailedItems.length}
          sublabel="items failed recently"
          color={data.recentFailedItems.length > 0 ? "red" : "green"}
        />
      </div>

      {/* Active Syncs */}
      {data.activeSyncs.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Active Syncs</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {data.activeSyncs.map((sync) => (
                <div
                  key={sync.connectorId}
                  className="border border-gray-100 rounded-lg p-4"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <span className="font-medium text-gray-900">
                        {sync.name || sync.connectorId.slice(0, 12)}
                      </span>
                      <Badge variant="info" className="ml-2">
                        {sync.type}
                      </Badge>
                    </div>
                    <span className="text-sm text-gray-500">
                      {sync.userName} &middot; {sync.orgName}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 mb-2">
                    <div className="flex-1">
                      <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-blue-500 rounded-full transition-all duration-500"
                          style={{ width: `${sync.progress}%` }}
                        />
                      </div>
                    </div>
                    <span className="text-sm font-medium text-gray-700 w-12 text-right">
                      {sync.progress}%
                    </span>
                  </div>
                  <div className="flex gap-4 text-xs text-gray-500">
                    <span>
                      <span className="text-green-600 font-medium">{sync.completed}</span> completed
                    </span>
                    <span>
                      <span className="text-amber-600 font-medium">{sync.pending}</span> pending
                    </span>
                    {sync.failed > 0 && (
                      <span>
                        <span className="text-red-600 font-medium">{sync.failed}</span> failed
                      </span>
                    )}
                    <span className="text-gray-400">
                      {sync.totalItems} total
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Two-column: Queue Status + Connector Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Queue Depths */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Queue Depths</CardTitle>
              <span className="text-xs text-gray-400">
                {data.queues.active.length} queues with pending work
              </span>
            </div>
          </CardHeader>
          <CardContent>
            {data.queues.active.length === 0 ? (
              <div className="text-center py-8 text-gray-400 text-sm">
                All queues idle
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead className="text-right">Waiting</TableHead>
                    <TableHead className="text-right">Active</TableHead>
                    <TableHead className="text-right">Delayed</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.queues.active.map((q) => (
                    <TableRow key={q.queueName}>
                      <TableCell>
                        <span className="font-mono text-xs text-gray-600">
                          {q.userId.slice(0, 16)}...
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <NumBadge value={q.waiting} color="amber" />
                      </TableCell>
                      <TableCell className="text-right">
                        <NumBadge value={q.active} color="blue" />
                      </TableCell>
                      <TableCell className="text-right">
                        <NumBadge value={q.delayed} color="gray" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            {/* Totals row */}
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100 text-sm">
              <span className="font-medium text-gray-700">Totals</span>
              <div className="flex gap-4">
                <span className="text-amber-600">{data.queues.totals.waiting} waiting</span>
                <span className="text-blue-600">{data.queues.totals.active} active</span>
                <span className="text-gray-500">{data.queues.totals.delayed} delayed</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Status Distribution */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Connector Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {data.connectors.statusCounts.map((s) => (
                  <StatusBar
                    key={s.status}
                    label={formatStatus(s.status)}
                    count={s.count}
                    total={data.connectors.statusCounts.reduce((a, b) => a + b.count, 0)}
                    color={STATUS_COLORS[s.status] || "default"}
                  />
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Document Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {data.connectorData.statusCounts.map((s) => (
                  <StatusBar
                    key={s.status}
                    label={formatStatus(s.status)}
                    count={s.count}
                    total={data.connectorData.statusCounts.reduce((a, b) => a + b.count, 0)}
                    color={STATUS_COLORS[s.status] || "default"}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Recent Failures */}
      {data.recentFailures.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Recently Failed Connectors</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Connector</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Organization</TableHead>
                  <TableHead>Last Synced</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.recentFailures.map((f) => (
                  <TableRow key={f.connectorId}>
                    <TableCell>
                      <div className="font-medium text-gray-900">
                        {f.name || f.connectorId.slice(0, 16)}
                      </div>
                      <div className="text-xs text-gray-400 font-mono">
                        {f.connectorId.slice(0, 20)}...
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="default">{f.type}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">{f.userName}</div>
                      <div className="text-xs text-gray-400">{f.userEmail}</div>
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {f.orgName}
                    </TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {f.lastSynced ? formatDate(f.lastSynced) : "Never"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Failed Items */}
      {data.recentFailedItems.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Failed Items (Last 24h)</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Item</TableHead>
                  <TableHead>Connector</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Retries</TableHead>
                  <TableHead>Failed At</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.recentFailedItems.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>
                      <div className="font-medium text-gray-900 max-w-[200px] truncate">
                        {item.name || item.id.slice(0, 16)}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {item.connectorName || "—"}
                    </TableCell>
                    <TableCell>
                      <Badge variant="default">{item.connectorType}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <span
                        className={`font-mono text-sm ${
                          item.retryCount >= 5 ? "text-red-600 font-bold" : "text-gray-600"
                        }`}
                      >
                        {item.retryCount}
                      </span>
                    </TableCell>
                    <TableCell className="text-sm text-gray-500">
                      {formatDate(item.updatedAt)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
      {/* Failed Jobs — with actual error messages from BullMQ */}
      {data.failedJobs && data.failedJobs.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Failed Jobs (Error Logs)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data.failedJobs.map((job, idx) => (
                <FailedJobRow key={`${job.jobId}-${idx}`} job={job} />
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ── Helper components ────────────────────────────────────────────────────────

function FailedJobRow({ job }: { job: FailedJob }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border border-red-100 rounded-lg p-3 bg-red-50/30">
      <div
        className="flex items-start justify-between cursor-pointer"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-mono text-xs text-gray-500">
              Job {job.jobId}
            </span>
            <Badge variant="error">
              attempt {job.attemptsMade}
            </Badge>
            {job.timestamp && (
              <span className="text-xs text-gray-400">
                {formatDate(new Date(job.timestamp).toISOString())}
              </span>
            )}
          </div>
          <div className="text-sm text-red-700 font-medium truncate">
            {job.failedReason}
          </div>
          {job.connectorDataId && (
            <div className="text-xs text-gray-400 mt-1 font-mono">
              connectorData: {job.connectorDataId.slice(0, 20)}...
            </div>
          )}
        </div>
        <button className="ml-2 text-gray-400 hover:text-gray-600 flex-shrink-0 mt-1">
          <svg
            className={`w-4 h-4 transition-transform ${expanded ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>
      {expanded && (
        <div className="mt-3 pt-3 border-t border-red-100">
          <div className="text-sm text-red-800 mb-2 break-words whitespace-pre-wrap">
            {job.failedReason}
          </div>
          {job.stacktrace.length > 0 && (
            <pre className="text-xs text-gray-500 bg-gray-50 rounded p-2 overflow-x-auto max-h-48">
              {job.stacktrace.join("\n")}
            </pre>
          )}
          <div className="flex gap-4 mt-2 text-xs text-gray-400">
            {job.connectorId && <span>Connector: {job.connectorId}</span>}
            <span>Queue: {job.queueName}</span>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({
  label,
  value,
  sublabel,
  color,
}: {
  label: string;
  value: number;
  sublabel: string;
  color: string;
}) {
  const colorMap: Record<string, string> = {
    blue: "bg-blue-50 text-blue-700",
    green: "bg-green-50 text-green-700",
    amber: "bg-amber-50 text-amber-700",
    red: "bg-red-50 text-red-700",
    gray: "bg-gray-50 text-gray-600",
  };
  const cls = colorMap[color] || colorMap.gray;

  return (
    <div className={`rounded-lg p-4 ${cls}`}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-sm font-medium">{label}</div>
      <div className="text-xs opacity-70 mt-1">{sublabel}</div>
    </div>
  );
}

function NumBadge({ value, color }: { value: number; color: string }) {
  if (value === 0) return <span className="text-gray-300">0</span>;
  const colorMap: Record<string, string> = {
    blue: "bg-blue-100 text-blue-700",
    amber: "bg-amber-100 text-amber-700",
    red: "bg-red-100 text-red-700",
    gray: "bg-gray-100 text-gray-600",
  };
  return (
    <span
      className={`inline-flex items-center justify-center min-w-[24px] px-1.5 py-0.5 rounded text-xs font-medium ${
        colorMap[color] || colorMap.gray
      }`}
    >
      {value}
    </span>
  );
}

function StatusBar({
  label,
  count,
  total,
  color,
}: {
  label: string;
  count: number;
  total: number;
  color: "default" | "success" | "warning" | "error" | "info";
}) {
  const pct = total > 0 ? (count / total) * 100 : 0;
  const barColor: Record<string, string> = {
    default: "bg-gray-400",
    success: "bg-green-500",
    warning: "bg-amber-500",
    error: "bg-red-500",
    info: "bg-blue-500",
  };
  return (
    <div className="flex items-center gap-3">
      <div className="w-28 text-sm text-gray-600 truncate">{label}</div>
      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full ${barColor[color]}`}
          style={{ width: `${Math.max(pct, 1)}%` }}
        />
      </div>
      <div className="w-16 text-right text-sm font-medium text-gray-700">
        {count.toLocaleString()}
      </div>
    </div>
  );
}

function formatStatus(status: string): string {
  return status
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (s) => s.toUpperCase())
    .trim();
}

function formatDate(date: string): string {
  return new Date(date).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
